import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  Chip,
} from '@mui/material';
import {
  CreateNewFolder as CreateFolderIcon,
  Search as SearchIcon,
  Sort as SortIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DriveFileMove as MoveIcon,
  History as HistoryIcon,
} from '@mui/icons-material';

import FolderTree from './FolderTree';
import FolderContents from './FolderContents';
import CreateFolderModal from './CreateFolderModal';
import EditFolderModal from './EditFolderModal';
import AddToFolderModal from './AddToFolderModal';
import ActivityLogModal from './ActivityLogModal';
import UploadFileModal from './UploadFileModal';
import { organizationState } from 'redux/organizationSlice';
import { loggedInUserState } from 'redux/userSlice';
import Snackbar from 'components/shared/Snackbar';
import DocumentViewerModal from 'components/shared/DocumentViewerModal';
import WordDocumentViewer from 'components/shared/WordDocumentViewer';
import fileCabinetService from 'services/fileCabinet';
import { getCertificateFiles } from 'services/appResources';
import { useDragDrop } from './DragDropProvider';
import { generateClient } from 'aws-amplify/api';
import { updateFolder } from 'graphql/fileCabinet';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNavPath, getNavigationPath } from 'constants/routes';
import { zipAndDownloadFolder } from 'utils/zipDownload';

const API = generateClient();

const FileCabinet = ({ documents, navigationTarget, onNavigationComplete }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedClient, userOrgType } = useSelector(organizationState);
  const userState = useSelector(loggedInUserState);
  const loggedInUser = userState?.loggedInUser || userState; // Handle nested structure
  const NAV_PATH = useNavPath(userOrgType);
  
  // State
  const [folders, setFolders] = useState([]);
  const [currentFolderID, setCurrentFolderID] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [folderContents, setFolderContents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('alphabetical'); // alphabetical, lastModified, dateCreated
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [message, setMessage] = useState(null);
  
  // Modals
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [editFolderOpen, setEditFolderOpen] = useState(false);
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [uploadFileOpen, setUploadFileOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [draggedFiles, setDraggedFiles] = useState([]);
  
  // Document viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  
  // Word document viewer state
  const [wordViewerOpen, setWordViewerOpen] = useState(false);
  const [selectedWordDocument, setSelectedWordDocument] = useState(null);
  
  // Check if user can manage folders (Client Admin only)
  // Temporarily allow all users to manage folders for testing
  // const canManageFolders = loggedInUser?.role === 'ClientAdmin';
  const canManageFolders = true; // TEMPORARY: Allow all users for testing
  
  // Debug log to check user role
  console.log('FileCabinet - User:', loggedInUser?.email, 'Role:', loggedInUser?.role, 'Can manage folders:', canManageFolders);
  
  // Helper function to check if a document is a Word document
  const isWordDocument = (document) => {
    if (!document.name) return false;
    const fileName = document.name.toLowerCase();
    return fileName.endsWith('.doc') || fileName.endsWith('.docx') || fileName.endsWith('.docm');
  };
  
  // Function to clean up duplicate items in a folder
  const cleanupDuplicates = async (folderID) => {
    try {
      const items = await fileCabinetService.getFolderContents(folderID);
      if (!items?.folderItems?.items) return;
      
      const seen = new Set();
      const duplicates = [];
      
      items.folderItems.items.forEach(item => {
        const certificateId = item.certificateID || item.documentID;
        if (seen.has(certificateId)) {
          duplicates.push(item);
        } else {
          seen.add(certificateId);
        }
      });
      
      if (duplicates.length > 0) {
        console.log(`Found ${duplicates.length} duplicates to remove:`, duplicates);
        
        for (const duplicate of duplicates) {
          try {
            await fileCabinetService.removeFromFolder(
              duplicate.id,
              folderID,
              loggedInUser.id,
              selectedClient.clientID,
              selectedClient.organizationID
            );
            console.log('Removed duplicate:', duplicate.id);
          } catch (error) {
            console.error('Failed to remove duplicate:', error);
          }
        }
        
        // Reload folder after cleanup
        loadFolders();
        loadFolderContents(folderID);
      }
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  };
  
  // Load folders on mount
  useEffect(() => {
    if (selectedClient?.clientID) {
      loadFolders();
    }
  }, [selectedClient]);
  
  // Handle navigation target from folder path clicks
  useEffect(() => {
    if (navigationTarget && navigationTarget.folderID && selectedClient?.clientID) {
      console.log('FileCabinet: Navigating to target folder:', navigationTarget);
      
      // Navigate directly to the target folder
      setCurrentFolderID(navigationTarget.folderID);
      
      // Build the folder path by finding the folder in the folders list
      const buildFolderPath = async () => {
        try {
          // First ensure folders are loaded
          if (folders.length === 0) {
            await loadFolders();
          }
          
          // For now, just set the path with the target folder
          // TODO: Build complete breadcrumb path by traversing parent folders
          const newPath = [{ id: navigationTarget.folderID, name: navigationTarget.folderName }];
          setFolderPath(newPath);
          
          // Load the folder contents
          loadFolderContents(navigationTarget.folderID);
          
          // Call the completion callback
          if (onNavigationComplete) {
            onNavigationComplete();
          }
        } catch (error) {
          console.error('Error building folder path:', error);
          if (onNavigationComplete) {
            onNavigationComplete();
          }
        }
      };
      
      buildFolderPath();
    }
  }, [navigationTarget, selectedClient, folders, onNavigationComplete]);
  
  // Load folder contents when current folder changes
  useEffect(() => {
    if (selectedClient?.clientID) {
      loadFolderContents(currentFolderID);
    }
  }, [currentFolderID, selectedClient]);
  
  // Load folders from backend
  const loadFolders = async () => {
    setLoading(true);
    try {
      const folders = await fileCabinetService.getFolders(
        selectedClient.clientID,
        currentFolderID
      );
      
      setFolders(folders);
    } catch (error) {
      console.error('Error loading folders:', error);
      
      // If it's a GraphQL error because the table doesn't exist yet, show a helpful message
      if (error.errors && error.errors[0]?.message) {
        const errorMessage = error.errors[0].message;
        console.log('GraphQL Error:', errorMessage);
        
        // Check for various table not found patterns
        if (errorMessage.includes('foldersByClient') || 
            errorMessage.includes('Validation error') ||
            errorMessage.includes('Cannot return null') ||
            errorMessage.includes('FILECABINETFOLDER')) {
          setMessage({
            severity: 'info',
            text: 'File Cabinet feature is ready but requires backend deployment. Run "amplify push" to create the database tables.',
          });
          // Set empty folders to show the UI
          setFolders([]);
        } else {
          setMessage({
            severity: 'error',
            text: 'Failed to load folders. Please try again.',
          });
        }
      } else {
        setMessage({
          severity: 'error',
          text: 'Failed to load folders',
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Navigate to folder
  const navigateToFolder = (folderID, folderName) => {
    if (folderID === null) {
      // Navigate to root
      setCurrentFolderID(null);
      setFolderPath([]);
    } else {
      // Navigate to specific folder
      setCurrentFolderID(folderID);
      // Update breadcrumb path
      const newPath = [...folderPath, { id: folderID, name: folderName }];
      setFolderPath(newPath);
    }
    // Load folder contents
    loadFolderContents(folderID);
  };
  
  // Load folder contents
  const loadFolderContents = async (folderID) => {
    try {
      if (!folderID) {
        // Root folder - show all folders but no documents
        setFolderContents([]);
        return;
      }
      
      // Load items in this folder
      const items = await fileCabinetService.getFolderContents(folderID);
      console.log('Loaded folder contents:', items);
      
      if (items && items.folderItems && items.folderItems.items) {
        console.log('Raw folder items from API:', items.folderItems.items);
        
        // Check for duplicates and filter them out
        const uniqueItems = [];
        const seenCertificateIds = new Set();
        const seenItemIds = new Set();
        
        items.folderItems.items.forEach(item => {
          const certificateId = item.certificateID || item.documentID;
          
          // Skip if we've already seen this certificate or item ID
          if (seenCertificateIds.has(certificateId) || seenItemIds.has(item.id)) {
            console.warn('Duplicate item found:', item);
            return;
          }
          
          seenCertificateIds.add(certificateId);
          seenItemIds.add(item.id);
          uniqueItems.push(item);
        });
        
        console.log('Unique items after deduplication:', uniqueItems);
        
        // Transform folder items to match document format for display
        const contents = uniqueItems.map(item => {
          // Find the actual certificate/document from the documents list if available
          const originalDoc = documents?.find(d => 
            d.id === item.certificateID || d.id === item.documentID
          );
          
          return {
            id: item.id, // This is the FILECABINETITEM id needed for removal
            itemId: item.id, // Keep both for clarity
            certificateID: item.certificateID,
            documentID: item.documentID,
            name: item.documentName,
            type: 'document', // Set type to 'document' for FolderContents filtering
            documentType: item.documentType, // Keep original document type
            status: originalDoc?.status || 'UNKNOWN',
            addedBy: item.addedByUser ? 
              `${item.addedByUser.firstName} ${item.addedByUser.lastName}` : 
              'Unknown',
            addedAt: item.createdAt,
            createdAt: item.createdAt,
            certificate: item.certificate,
          };
        });
        
        console.log('Transformed contents:', contents);
        console.log('Actual item count in folder:', contents.length);
        
        // Update the folder's item count to match actual contents
        if (contents.length !== items.itemCount) {
          console.warn(`Item count mismatch: folder shows ${items.itemCount}, but contains ${contents.length} items`);
          
          // Update folder item count to match reality
          try {
            await API.graphql({
              query: updateFolder,
              variables: {
                input: {
                  id: folderID,
                  itemCount: contents.length,
                  lastActivityAt: new Date().toISOString(),
                },
              },
            });
            console.log('Updated folder item count to:', contents.length);
            
            // Reload folders to reflect the updated count in the UI
            setTimeout(() => {
              loadFolders();
            }, 100);
            
          } catch (error) {
            console.error('Failed to update folder item count:', error);
          }
        }
        
        setFolderContents(contents);
      } else {
        console.log('No folder items found');
        setFolderContents([]);
      }
    } catch (error) {
      console.error('Error loading folder contents:', error);
      setMessage({
        severity: 'error',
        text: 'Failed to load folder contents',
      });
      setFolderContents([]);
    }
  };
  
  // Handle folder creation
  const handleCreateFolder = async (folderData) => {
    try {
      const newFolder = await fileCabinetService.createFolder({
        ...folderData,
        createdBy: loggedInUser.id,
        organizationID: selectedClient.organizationID,
      });
      
      setMessage({
        severity: 'success',
        text: `Folder "${folderData.name}" created successfully`,
      });
      
      // Reload folders
      loadFolders();
      setCreateFolderOpen(false);
    } catch (error) {
      console.error('Error creating folder:', error);
      
      // Check if it's a table not found error
      if (error.errors && error.errors[0]?.message) {
        const errorMessage = error.errors[0].message;
        if (errorMessage.includes('createFILECABINETFOLDER') || 
            errorMessage.includes('Validation error') ||
            errorMessage.includes('Cannot return null')) {
          setMessage({
            severity: 'warning',
            text: 'File Cabinet tables not deployed yet. Please run "amplify push" to deploy the backend.',
          });
        } else {
          setMessage({
            severity: 'error',
            text: error.message || 'Failed to create folder',
          });
        }
      } else {
        setMessage({
          severity: 'error',
          text: error.message || 'Failed to create folder',
        });
      }
    }
  };
  
  // Handle folder edit
  const handleEditFolder = async (folderData) => {
    try {
      await fileCabinetService.updateFolder(
        folderData.id,
        {
          name: folderData.name,
          description: folderData.description,
        },
        loggedInUser.id
      );
      
      setMessage({
        severity: 'success',
        text: `Folder "${folderData.name}" updated successfully`,
      });
      
      // Reload folders
      loadFolders();
      setEditFolderOpen(false);
    } catch (error) {
      console.error('Error updating folder:', error);
      setMessage({
        severity: 'error',
        text: error.message || 'Failed to update folder',
      });
    }
  };
  
  // Handle folder deletion
  const handleDeleteFolder = async (folderID) => {
    if (!window.confirm('Are you sure you want to delete this folder? All items will be removed.')) {
      return;
    }
    
    try {
      await fileCabinetService.deleteFolder(
        folderID,
        loggedInUser.id,
        selectedClient.clientID,
        selectedClient.organizationID
      );
      
      setMessage({
        severity: 'success',
        text: 'Folder deleted successfully',
      });
      
      // Reload folders
      loadFolders();
    } catch (error) {
      console.error('Error deleting folder:', error);
      setMessage({
        severity: 'error',
        text: error.message || 'Failed to delete folder',
      });
    }
  };
  
  // Handle zip and download folder
  const handleZipDownloadFolder = async (folder) => {
    try {
      setMessage({
        severity: 'info',
        text: `Creating zip file for "${folder.name}"...`,
      });
      
      await zipAndDownloadFolder(folder, selectedClient.clientID, (progress) => {
        if (progress.status === 'error') {
          setMessage({
            severity: 'error',
            text: progress.message,
          });
        } else if (progress.status === 'complete') {
          setMessage({
            severity: 'success',
            text: `Successfully downloaded "${folder.name}.zip"`,
          });
        } else if (progress.status === 'compressing') {
          // Optional: show progress in UI
          console.log(progress.message);
        }
      });
    } catch (error) {
      console.error('Error downloading folder:', error);
      setMessage({
        severity: 'error',
        text: `Failed to download folder: ${error.message}`,
      });
    }
  };
  
  // Handle viewing document
  const handleViewDocument = async (document) => {
    console.log('View document:', document);
    
    // Check if this is an uploaded file (has documentType: 'uploaded_file' or certificateID starts with 'file_')
    if (document.documentType === 'uploaded_file' || (document.certificateID && document.certificateID.startsWith('file_'))) {
      console.log('Viewing uploaded file:', document);
      setLoadingDocument(true);
      
      try {
        // For uploaded files, we need to reconstruct the file path
        // Pattern: protected/file-cabinet/{clientID}/{folderID}/{filename}
        // The filename includes a timestamp to avoid conflicts: {baseName}_{timestamp}.{extension}
        
        // Extract timestamp from the certificateID (format: file_{timestamp})
        const timestamp = document.certificateID.replace('file_', '');
        const fileExtension = document.name.split('.').pop();
        const baseName = document.name.replace(`.${fileExtension}`, '');
        const fileName = `${baseName}_${timestamp}.${fileExtension}`;
        
        // Reconstruct the file path
        const filePath = `protected/file-cabinet/${selectedClient.clientID}/${currentFolderID}/${fileName}`;
        
        console.log('Reconstructed file path:', filePath);
        
        // Get the file URL from S3 using the reconstructed file path
        const fileUrl = await fileCabinetService.getUploadedFileUrl(filePath);
        
        if (isWordDocument(document)) {
          // Open Word document viewer
          setSelectedWordDocument({
            name: document.name,
            fileUrl: fileUrl
          });
          setWordViewerOpen(true);
        } else {
          // Open PDF/generic document viewer
          setSelectedDocument({
            name: document.name,
            fileUrl: fileUrl
          });
          setViewerOpen(true);
        }
      } catch (error) {
        console.error('Error loading uploaded file:', error);
        setMessage({
          severity: 'error',
          text: `Failed to load uploaded file: ${error.message}`,
        });
      } finally {
        setLoadingDocument(false);
      }
      return;
    }
    
    // Find the original document from the documents list
    let originalDoc = documents?.find(d => 
      d.id === document.certificateID || d.id === document.documentID
    );
    
    // If not found, try to find by name as fallback
    if (!originalDoc) {
      originalDoc = documents?.find(d => d.name === document.name);
    }
    
    if (!originalDoc) {
      console.error('Original document not found for:', document);
      setMessage({
        severity: 'error',
        text: 'Unable to locate document for viewing',
      });
      return;
    }
    
    console.log('Found original document:', originalDoc);
    
    if (originalDoc.type === 'certificate' && originalDoc.certificateData) {
      // Navigate to certificate details page
      const path = getNavigationPath(NAV_PATH.CLIENT_CERTIFICATE_DETAILS, {
        clientId: selectedClient.organizationID,
        vendorId: originalDoc.certificateData.clientVendorID,
        certificateId: originalDoc.id
      });
      navigate(path, { state: { referrer: location.pathname } });
    } else {
      // Open PDF viewer for other documents
      setLoadingDocument(true);
      try {
        // Get the document URL based on type
        let files = [];
        if (originalDoc.supplementalData && originalDoc.parentCertificateId) {
          // This is a supplemental document
          files = [{
            name: originalDoc.supplementalData.filename,
            isSupplementalCoiFile: true,
            fullPath: `SupplementalCoverageDocumentation/${originalDoc.supplementalData.insuranceType}/${originalDoc.supplementalData.documentType}/${originalDoc.supplementalData.filename}`
          }];
        } else if (originalDoc.fileData && originalDoc.parentCertificateId) {
          // This is a regular file from the files array
          files = [originalDoc.fileData];
        } else if (originalDoc.type === 'certificate') {
          // This is a certificate - try to get its PDF file
          const certificateId = originalDoc.certificateData?.id || originalDoc.id;
          if (certificateId) {
            // Try to load the certificate's main file
            files = [{
              name: `${originalDoc.name}.pdf`,
              fullPath: `certificates/${certificateId}/certificate.pdf`
            }];
          }
        } else if (originalDoc.type === 'document' && !originalDoc.parentCertificateId) {
          console.warn('Standalone document without parent certificate:', originalDoc);
          setMessage({
            severity: 'warning',
            text: 'This document type cannot be viewed directly',
          });
          return;
        }
        
        if (files.length > 0 && (originalDoc.parentCertificateId || originalDoc.certificateData)) {
          const certificateId = originalDoc.parentCertificateId || originalDoc.certificateData?.id || originalDoc.id;
          const result = await getCertificateFiles(certificateId, files);
          
          if (result && result.length > 0) {
            if (isWordDocument(document)) {
              // Open Word document viewer
              setSelectedWordDocument({
                name: document.name,
                fileUrl: result[0].content
              });
              setWordViewerOpen(true);
            } else {
              // Open PDF/generic document viewer
              setSelectedDocument({
                name: document.name,
                fileUrl: result[0].content
              });
              setViewerOpen(true);
            }
          } else {
            setMessage({
              severity: 'warning',
              text: 'Document file not found or unavailable',
            });
          }
        } else {
          setMessage({
            severity: 'warning',
            text: 'Unable to determine document file location',
          });
        }
      } catch (error) {
        console.error('Error loading document:', error);
        setMessage({
          severity: 'error',
          text: 'Failed to load document for viewing',
        });
      } finally {
        setLoadingDocument(false);
      }
    }
  };
  
  // Handle downloading document
  const handleDownloadDocument = (document) => {
    // Download document
    console.log('Download document:', document);
    // TODO: Implement document download
  };
  
  // Handle removing document from folder
  const handleRemoveDocument = async (document, folderID) => {
    try {
      const result = await fileCabinetService.removeFromFolder(
        document.id,
        folderID,
        loggedInUser.id,
        selectedClient.clientID,
        selectedClient.organizationID
      );
      
      setMessage({
        severity: 'success',
        text: `Document "${document.name}" removed from folder`,
      });
      
      // Reload folders to update item counts
      loadFolders();
      
      // Reload folder contents
      loadFolderContents(folderID);
    } catch (error) {
      console.error('Error removing document from folder:', error);
      setMessage({
        severity: 'error',
        text: 'Failed to remove document from folder',
      });
    }
  };
  
  // Handle document drop on folder
  const handleDropDocument = async (folderID, document) => {
    try {
      const result = await fileCabinetService.addToFolder(
        folderID,
        document,
        loggedInUser.id,
        selectedClient.clientID,
        selectedClient.organizationID
      );
      
      if (result.duplicate) {
        setMessage({
          severity: 'warning',
          text: `Document "${document.name}" is already in this folder`,
        });
        return; // Don't reload if it was a duplicate
      } else if (result.alreadyInFolders && result.alreadyInFolders > 0) {
        setMessage({
          severity: 'info',
          text: `Document added to folder. Note: This document is also in ${result.alreadyInFolders} other folder(s).`,
        });
      } else {
        setMessage({
          severity: 'success',
          text: `Document "${document.name}" added to folder successfully`,
        });
      }
      
      // Reload folders to update item counts
      loadFolders();
      
      // If viewing the target folder, reload its contents
      if (currentFolderID === folderID) {
        loadFolderContents(folderID);
      }
    } catch (error) {
      console.error('Error adding document to folder:', error);
      setMessage({
        severity: 'error',
        text: 'Failed to add document to folder',
      });
    }
  };

  // Handle folder upload (from menu or drag-and-drop)
  const handleFolderUpload = (folder, draggedFilesParam = null) => {
    setSelectedFolder(folder);
    setDraggedFiles(draggedFilesParam || []);
    setUploadFileOpen(true);
  };

  // Handle file upload
  const handleFileUpload = async (file, folder) => {
    try {
      console.log('Uploading file:', file.name, 'to folder:', folder.name);
      
      // Use the file upload service to upload file to S3 and add to folder
      const result = await fileCabinetService.uploadFileToFolder(
        file,
        folder,
        loggedInUser.id,
        selectedClient.clientID,
        selectedClient.organizationID
      );
      
      console.log('File upload result:', result);
      
      setMessage({
        severity: 'success',
        text: `File "${file.name}" uploaded successfully to folder "${folder.name}"`,
      });
      
      // Reload folder contents and folders to update counts
      loadFolders();
      if (currentFolderID === folder.id) {
        loadFolderContents(folder.id);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setMessage({
        severity: 'error',
        text: `Failed to upload file "${file.name}": ${error.message}`,
      });
      throw error; // Re-throw so the modal can handle it
    }
  };
  
  // Sort folders
  const sortFolders = (foldersToSort) => {
    const sorted = [...foldersToSort];
    
    switch (sortBy) {
      case 'alphabetical':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'lastModified':
        sorted.sort((a, b) => new Date(b.lastActivityAt) - new Date(a.lastActivityAt));
        break;
      case 'dateCreated':
        sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        break;
    }
    
    return sorted;
  };
  
  // Filter folders by search
  const filterFolders = (foldersToFilter) => {
    if (!searchTerm) return foldersToFilter;
    
    const term = searchTerm.toLowerCase();
    return foldersToFilter.filter(folder => 
      folder.name.toLowerCase().includes(term) ||
      (folder.description && folder.description.toLowerCase().includes(term))
    );
  };
  
  const displayedFolders = sortFolders(filterFolders(folders));
  
  return (
    <Box className="file-cabinet-container" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">
            File Cabinet
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setActivityLogOpen(true)}
              size="small"
            >
              Activity Log
            </Button>
            {canManageFolders && (
              <Button
                className="file-cabinet-create-folder"
                variant="contained"
                startIcon={<CreateFolderIcon />}
                onClick={() => setCreateFolderOpen(true)}
                size="small"
              >
                New Folder
              </Button>
            )}
          </Stack>
        </Stack>
        
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link
            component="button"
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigateToFolder(null, null)}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Root
          </Link>
          {folderPath.map((folder, index) => (
            <Link
              key={folder.id}
              component="button"
              underline="hover"
              color={index === folderPath.length - 1 ? 'text.primary' : 'inherit'}
              onClick={() => {
                if (index < folderPath.length - 1) {
                  const newPath = folderPath.slice(0, index + 1);
                  setFolderPath(newPath);
                  setCurrentFolderID(folder.id);
                  loadFolderContents(folder.id);
                }
              }}
              sx={{ cursor: index < folderPath.length - 1 ? 'pointer' : 'default' }}
            >
              {folder.name}
            </Link>
          ))}
        </Breadcrumbs>
        
        {/* Search and Sort */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search folders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 400 }}
          />
          
          <Button
            size="small"
            startIcon={<SortIcon />}
            onClick={(e) => setSortMenuAnchor(e.currentTarget)}
            variant="outlined"
          >
            Sort: {sortBy === 'alphabetical' ? 'A-Z' : sortBy === 'lastModified' ? 'Last Modified' : 'Date Created'}
          </Button>
          
          <Menu
            anchorEl={sortMenuAnchor}
            open={Boolean(sortMenuAnchor)}
            onClose={() => setSortMenuAnchor(null)}
          >
            <MenuItem 
              selected={sortBy === 'alphabetical'}
              onClick={() => {
                setSortBy('alphabetical');
                setSortMenuAnchor(null);
              }}
            >
              Alphabetical
            </MenuItem>
            <MenuItem 
              selected={sortBy === 'lastModified'}
              onClick={() => {
                setSortBy('lastModified');
                setSortMenuAnchor(null);
              }}
            >
              Last Modified
            </MenuItem>
            <MenuItem 
              selected={sortBy === 'dateCreated'}
              onClick={() => {
                setSortBy('dateCreated');
                setSortMenuAnchor(null);
              }}
            >
              Date Created
            </MenuItem>
          </Menu>
        </Stack>
      </Box>
      
      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2, minHeight: 0 }}>
        {/* Folder Tree - Left Panel */}
        <Paper className="file-cabinet-folder-tree" sx={{ width: 300, p: 2, overflow: 'auto' }}>
          <Typography variant="subtitle2" gutterBottom>
            Folders
          </Typography>
          <FolderTree
            folders={displayedFolders}
            currentFolderID={currentFolderID}
            onFolderClick={navigateToFolder}
            onFolderEdit={(folder) => {
              setSelectedFolder(folder);
              setEditFolderOpen(true);
            }}
            onFolderDelete={handleDeleteFolder}
            onFolderUpload={handleFolderUpload}
            onFolderZipDownload={handleZipDownloadFolder}
            canManage={canManageFolders}
            loading={loading}
            onDropDocument={handleDropDocument}
          />
        </Paper>
        
        {/* Folder Contents - Right Panel */}
        <Paper className="file-cabinet-contents" sx={{ flex: 1, p: 2, overflow: 'auto' }}>
          <FolderContents
            folderID={currentFolderID}
            contents={folderContents}
            documents={documents}
            onNavigateToFolder={navigateToFolder}
            canManage={canManageFolders}
            loading={loading}
            onRemoveDocument={handleRemoveDocument}
            onViewDocument={handleViewDocument}
            onDownloadDocument={handleDownloadDocument}
          />
        </Paper>
      </Box>
      
      {/* Modals */}
      <CreateFolderModal
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        onSubmit={handleCreateFolder}
        parentFolderID={currentFolderID}
        currentDepth={folderPath.length}
        maxDepth={5}
        clientID={selectedClient?.clientID}
      />
      
      <EditFolderModal
        open={editFolderOpen}
        onClose={() => setEditFolderOpen(false)}
        onSubmit={handleEditFolder}
        folder={selectedFolder}
      />
      
      <ActivityLogModal
        open={activityLogOpen}
        onClose={() => setActivityLogOpen(false)}
        clientID={selectedClient?.clientID}
      />
      
      <UploadFileModal
        open={uploadFileOpen}
        onClose={() => {
          setUploadFileOpen(false);
          setSelectedFolder(null);
          setDraggedFiles([]);
        }}
        onUpload={handleFileUpload}
        folder={selectedFolder}
        initialFiles={draggedFiles}
      />
      
      {/* Document Viewer Modal */}
      {selectedDocument && (
        <DocumentViewerModal
          open={viewerOpen}
          onClose={() => {
            setViewerOpen(false);
            setSelectedDocument(null);
          }}
          document={selectedDocument}
          onDownload={() => {
            // Handle download
            const link = document.createElement('a');
            link.href = selectedDocument.fileUrl;
            link.download = selectedDocument.name;
            link.click();
          }}
        />
      )}
      
      {/* Word Document Viewer */}
      {selectedWordDocument && (
        <WordDocumentViewer
          open={wordViewerOpen}
          onClose={() => {
            setWordViewerOpen(false);
            setSelectedWordDocument(null);
          }}
          documentUrl={selectedWordDocument.fileUrl}
          documentName={selectedWordDocument.name}
          onDownload={() => {
            // Handle download
            const link = document.createElement('a');
            link.href = selectedWordDocument.fileUrl;
            link.download = selectedWordDocument.name;
            link.click();
          }}
        />
      )}
      
      {/* Document Loading Overlay */}
      {loadingDocument && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 10,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={48} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Loading document...
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Snackbar for messages */}
      <Snackbar
        message={message}
        setMessage={setMessage}
      />
    </Box>
  );
};

export default FileCabinet;